// Ítem de Instagram en Mi marca, al lado del de Tiendanube y con sus mismas piezas
// (ADR-2026-09-24-instagram-boton-en-mi-marca):
//   • Sin conectar → "Conectar" + modal que explica qué va a ver Atlas.
//   • Conectada → "@usuario · Desconectar" (modal de confirmación).
//   • Vencida → "La conexión venció · Reconectar".
// El estado lo trae `estadoDeInstagram` (liviano: lee nuestra base, no Meta).
// Conectar/desconectar via `lib/instagramConexion`. El token nunca pasa por acá.

import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, Instagram, Loader2, X } from "lucide-react";
import {
  abrirVentanaDeInstagram,
  conectarInstagramEnVentana,
  desconectarInstagram,
  ErrorDeInstagram,
  estadoDeInstagram,
  type EstadoDeInstagram,
} from "../lib/instagramConexion";
import { ChipDeIntegracion } from "./ChipDeIntegracion";

const ICONO = "h-[18px] w-[18px] shrink-0 text-[#c13584]";

export const InstagramStatus: React.FC<{
  companyId: string | null;
  brandId: string | null;
  /** "chip" (cabecera de Mi marca, escritorio) o "boton" (Mi marca mobile). Misma lógica, distinto disparador. */
  variant?: "chip" | "boton";
}> = ({ companyId, brandId, variant = "chip" }) => {
  const [estado, setEstado] = React.useState<EstadoDeInstagram | null>(null);
  const [mostrarConectar, setMostrarConectar] = React.useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = React.useState(false);
  const [conectando, setConectando] = React.useState(false);
  const [desconectando, setDesconectando] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [aviso, setAviso] = React.useState<string | null>(null);
  const [verPasos, setVerPasos] = React.useState(false);
  // La marca de AHORA: lo que vuelve de una acción empezada con otra marca no se muestra.
  const marcaActualRef = React.useRef(brandId);
  marcaActualRef.current = brandId;

  React.useEffect(() => {
    let cancelado = false;
    // Al cambiar de marca se olvida TODO lo de la anterior antes de preguntar: si no,
    // durante la consulta se veía su @usuario y "Desconectar" borraba la conexión de la
    // marca NUEVA (review Greptile #140). Con `estado` en null la fila no se dibuja, y
    // los modales se cierran para que no reaparezcan cuando llegue el estado nuevo.
    setEstado(null);
    setMostrarConectar(false);
    setMostrarConfirmar(false);
    setAviso(null);
    setError(null);
    setVerPasos(false);
    if (!companyId) return;
    void estadoDeInstagram(brandId).then((s) => {
      if (!cancelado) setEstado(s);
    });
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId]);

  // Cargando, sin empresa, o no pudimos preguntar: mejor no mostrar nada que
  // mostrar "sin conectar" a quien sí lo tiene conectado.
  if (!estado || estado.error) return null;

  const vencida = !estado.conectada && estado.estado === "vencida";
  const usuario = estado.username ? `@${estado.username}` : "Conectada";

  const conectar = async () => {
    if (conectando) return;
    // ⚠️ La ventana se abre ACÁ, sincrónicamente dentro del click, ANTES de cualquier
    // `await`: Safari y los navegadores de iPhone la bloquean si no
    // (ADR-2026-08-07-tiendanube-popup-safari). No mover esta línea.
    const popup = abrirVentanaDeInstagram();
    setConectando(true);
    setError(null);
    setAviso(null);
    try {
      const marca = brandId;
      const r = await conectarInstagramEnVentana(marca, popup);
      const nuevo = await estadoDeInstagram(marca);
      if (marcaActualRef.current !== marca) return; // cambió de marca mientras conectaba
      setEstado(nuevo);
      setMostrarConectar(false);
      if (r.faltanMetricas) {
        setAviso(
          "Conectamos tu Instagram, pero no aceptaste ver las métricas. Para que Atlas te diga cómo te va, volvé a conectarlo y aceptá ese permiso.",
        );
      }
    } catch (e) {
      // Cerrar la ventana a propósito no es un error que haya que cantarle.
      if (e instanceof ErrorDeInstagram && e.code === "ventana_cerrada") setError(null);
      else setError(e instanceof Error ? e.message : "No se pudo conectar.");
    } finally {
      setConectando(false);
    }
  };

  const confirmarDesconectar = async () => {
    if (desconectando) return;
    setDesconectando(true);
    try {
      const marca = brandId;
      await desconectarInstagram(marca);
      const nuevo = await estadoDeInstagram(marca);
      if (marcaActualRef.current !== marca) return;
      setEstado(nuevo);
      setMostrarConfirmar(false);
    } catch {
      /* si falla, queda como estaba */
    } finally {
      setDesconectando(false);
    }
  };

  const abrirConectar = () => {
    setError(null);
    setMostrarConectar(true);
  };

  const accion = estado.conectada ? (
    <button
      type="button"
      onClick={() => setMostrarConfirmar(true)}
      className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
    >
      Desconectar
    </button>
  ) : (
    <button
      type="button"
      onClick={abrirConectar}
      className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
    >
      {vencida ? "Reconectar" : "Conectar"}
    </button>
  );

  const subtitulo = estado.conectada ? usuario : vencida ? "La conexión venció" : null;

  return (
    <>
      {variant === "boton" ? (
        !estado.conectada && !vencida ? (
          // ── Fila completa (mobile), la misma caja que la de Tiendanube ──
          <button
            type="button"
            onClick={abrirConectar}
            className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.07] bg-white px-3 py-3 text-left transition-opacity active:opacity-70"
          >
            <Instagram className={ICONO} />
            <span className="flex-1 text-[14px] font-medium text-brand-black/85">Conectar Instagram</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-black/30" />
          </button>
        ) : (
          <div
            className="flex w-full items-center justify-between gap-2 rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <Instagram className={ICONO} />
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-brand-black/80 leading-tight">Instagram</div>
                {subtitulo && (
                  <div className="text-[12px] text-brand-black/45 truncate leading-tight">{subtitulo}</div>
                )}
              </div>
            </div>
            {accion}
          </div>
        )
      ) : (
        // Chip de la cabecera (Mi marca escritorio) — ADR-2026-10-02-mi-marca-cabecera-de-identidad.
        <ChipDeIntegracion
          clave="instagram"
          icono={<Instagram className="h-4 w-4 shrink-0 text-[#c13584]" />}
          nombre="Instagram"
          estado={estado.conectada ? "conectada" : vencida ? "vencida" : "desconectada"}
          detalle={usuario}
          onConectar={abrirConectar}
          onDesconectar={() => setMostrarConfirmar(true)}
        />
      )}

      {/* En la fila de chips de la cabecera el aviso va en su propio renglón
          (`basis-full`): si no, la fila lo trataría como un chip más. */}
      {aviso && (
        <p
          className={`mt-1.5 px-1 text-[12px] leading-snug text-amber-700 ${variant === "chip" ? "basis-full max-w-[420px] " : ""}`}
        >
          {aviso}
        </p>
      )}

      {/* Modal: conectar (qué va a ver Atlas y qué no) */}
      {mostrarConectar &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]">
            <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  {vencida ? "Reconectar Instagram" : "Conectar Instagram"}
                </h3>
                <button
                  type="button"
                  onClick={() => !conectando && setMostrarConectar(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6 space-y-3 text-[length:var(--text-body)] text-neutral-600">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto">
                  <Instagram className="h-5 w-5 text-[#c13584]" />
                </div>
                <p>
                  Atlas va a poder ver los números de tu cuenta: alcance, vistas, tus mejores
                  posts y quién te sigue. Así le podés preguntar al chat cómo te va.
                </p>
                <p>No publica, no responde y no toca nada de tu cuenta.</p>
                <p className="text-[13px] text-neutral-500">
                  Tu cuenta tiene que ser profesional (de empresa o de creador). Se abre una
                  ventana de Instagram para que lo autorices.
                </p>
                <div className="text-[13px]">
                  <button
                    type="button"
                    onClick={() => setVerPasos((v) => !v)}
                    aria-expanded={verPasos}
                    className="inline-flex items-center gap-1 text-brand-primary underline underline-offset-2 hover:opacity-80"
                  >
                    ¿Cómo paso mi cuenta a profesional?
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform ${verPasos ? "rotate-180" : ""}`}
                    />
                  </button>
                  {verPasos && (
                    <ol className="mt-2 list-decimal space-y-1 pl-5 text-neutral-600">
                      <li>Abrí Instagram en el celular y entrá a tu perfil.</li>
                      <li>
                        Tocá el menú <span className="font-medium">☰</span> (arriba a la derecha) →{" "}
                        <span className="font-medium">Configuración y privacidad</span>.
                      </li>
                      <li>
                        Entrá a <span className="font-medium">Tipo de cuenta y herramientas</span> →{" "}
                        <span className="font-medium">Cambiar a cuenta profesional</span>.
                      </li>
                      <li>
                        Elegí una categoría (por ejemplo, &quot;Tienda de ropa&quot;) y después{" "}
                        <span className="font-medium">Empresa</span>.
                      </li>
                      <li>Listo. Es gratis y no perdés seguidores ni publicaciones. Volvé acá y tocá Conectar.</li>
                    </ol>
                  )}
                </div>
                {error && <p className="text-[13px] text-red-600">{error}</p>}
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setMostrarConectar(false)}
                  disabled={conectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={conectar}
                  disabled={conectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
                >
                  {conectando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {conectando ? "Conectando…" : "Conectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Modal: confirmar desconexión */}
      {mostrarConfirmar &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]">
            <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  Desconectar Instagram
                </h3>
                <button
                  type="button"
                  onClick={() => !desconectando && setMostrarConfirmar(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <Instagram className="h-5 w-5 text-[#c13584]" />
                </div>
                <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                  ¿Desconectar{" "}
                  <span className="font-medium text-neutral-800">{usuario}</span> de esta marca?
                  El chat deja de ver tus números. Vas a poder volver a conectarla cuando quieras.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(false)}
                  disabled={desconectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmarDesconectar}
                  disabled={desconectando}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-60"
                >
                  {desconectando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {desconectando ? "Desconectando…" : "Desconectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default InstagramStatus;
